import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import toast from '../../../../utils/toast';
import { createItem, fetchAllItems } from '../../../requests/requests';
import {
  palette,
  spacing,
  radius,
  shadow,
  layout,
  status,
} from '../../../../style-utils/theme';
import { InviteForm } from '../../../onboarding/invite/invite-form';
import { FormModal } from '../../../modal/form-modal';
import { ROLES } from '../../../../auth/roles';
import { AppButton } from '../../../button/app-button';
import { down } from '../../../../style-utils/media-queries';
import { InlineMessage } from '../../../../motion';

interface Building {
  id: number;
  property_name: string;
  address: string;
}

// Block-body prop readers (kept out of the styled template so prettier never
// wraps a long ternary inside it).
const tabBg = (p: { active?: boolean }) => {
  /* lagoon, not the primary teal: this pill carries a white 14px label, and
     white on #00A19A is 3.20:1 — under the 4.5:1 that size needs. On Deep
     Lagoon the same label reads at 4.9:1 and the pill still looks like the
     brand. */
  return p.active ? palette.lagoon : 'transparent';
};
const tabColor = (p: { active?: boolean }) => {
  return p.active ? palette.white : palette.slate;
};

interface TenantForm {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  // Client-side only. It is in the form object rather than beside it because
  // this form is small and `submit` sends named fields, not the whole object —
  // so a confirm field here cannot leak into the request the way it would on
  // the user form, which serialises its state wholesale.
  password_confirm: string;
  building_id: string;
}

interface DashboardAddNewTenantProps {
  // WP-26. Same latent defect as the add-user modal: the residents list is
  // rendered behind this form by an unkeyed fragment, so its fiber is reused
  // and nothing refetches when this saves.
  onSaved?: () => void;
}

export const DashboardAddNewTenant: React.FC<DashboardAddNewTenantProps> = ({
  onSaved,
}) => {
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [form, setForm] = useState<TenantForm>({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: '',
    building_id: '',
  });
  const [saving, setSaving] = useState(false);
  const saveRef = useRef(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'invite' | 'password'>('invite');

  useEffect(() => {
    let active = true;

    fetchAllItems('dashboard/buildings')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((data: any) => {
        if (active) {
          setBuildings(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (active) {
          setBuildings([]);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const change = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm(current => ({ ...current, [e.target.name]: e.target.value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (saveRef.current) {
      return;
    }

    setError('');

    if (
      !form.first_name ||
      !form.last_name ||
      !form.email ||
      !form.password ||
      !form.building_id
    ) {
      setError('All fields are required.');

      return;
    }
    if (form.password.length < 8) {
      setError('Temporary password must be at least 8 characters.');

      return;
    }
    // The password is set FOR SOMEBODY WHO IS NOT HERE, and they cannot try it
    // before they are handed it. A typo produced an account nobody could sign
    // in to and an owner who had never seen the password either.
    if (form.password !== form.password_confirm) {
      setError('The two passwords do not match.');

      return;
    }

    saveRef.current = true;
    setSaving(true);
    try {
      // Named fields, NOT `...form`: password_confirm is a client-side check
      // and has no business in the request body.
      const result = (await createItem('dashboard/tenants/add-new-tenant', {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
        building_id: Number(form.building_id),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      })) as any;
      const label = [form.first_name, form.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      const who = label || 'Resident';

      // SAY WHICH OF THE THREE THINGS HAPPENED (WP-48 / R-19).
      //
      // This address may already belong to somebody in this organisation — a
      // resident taking a second unit, or one who left and came back — in which
      // case the server ADOPTS that identity rather than minting a second one.
      // Reporting "added" for all three would hide two facts the person at the
      // desk needs: that the password they just typed was not used, and that a
      // sign-in which had been switched off is live again. R-19a permits the
      // second; it does not permit it happening quietly.
      if (result?.login_restored) {
        toast.success(
          `${who} added. They already had an account here, so their existing ` +
            'sign-in has been restored and the password you entered was not used.'
        );
      } else if (result?.adopted) {
        toast.success(
          `${who} added to this building. They already had an account here, so ` +
            'their existing sign-in is unchanged and the password you entered ' +
            'was not used.'
        );
      } else {
        toast.success(label ? `${label} added` : 'Resident added');
      }
      onSaved?.();
      navigate('/dashboard/residents');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Could not add resident';
      setError(msg);
      toast.error(msg);
    } finally {
      saveRef.current = false;
      setSaving(false);
    }
  };

  // Every exit — Cancel, the ×, Escape, the scrim — goes back to the list.
  const close = () => navigate('/dashboard/residents');

  return (
    <FormModal
      /* Any field with something in it. `building_id` counts too — picking the
         building is a decision the reader made and would have to make again. */
      isDirty={Object.values(form).some(value => String(value || '').length > 0)}
      discardMessage="This resident has not been created yet. Close and lose what you have entered?"
      title="Add new resident"
      subtitle={
        mode === 'invite'
          ? 'Send an invitation and let them set their own password.'
          : 'Set a temporary password for them now.'
      }
      wide
      onClose={close}
      footer={
        // The invite pane has its own send button; a second one at the foot
        // would be a control that does not apply to what is on screen.
        mode === 'password' ? (
          <>
            <Cancel variant="secondary" onClick={close}>
              Cancel
            </Cancel>
            <Save
              type="submit"
              form="add-new-tenant-form"
              loading={saving}
              loadingLabel="Adding resident"
            >
              Add resident
            </Save>
          </>
        ) : null
      }
    >
      <ModeTabs>
        <ModeTab
          type="button"
          active={mode === 'invite'}
          aria-pressed={mode === 'invite'}
          onClick={() => setMode('invite')}
        >
          Send invite
        </ModeTab>
        <ModeTab
          type="button"
          active={mode === 'password'}
          aria-pressed={mode === 'password'}
          onClick={() => setMode('password')}
        >
          Set a password
        </ModeTab>
      </ModeTabs>

      {mode === 'invite' && (
        <InviteWrap>
          <InviteForm
            allowedRoles={[ROLES.RESIDENT]}
            buildings={buildings.map(b => ({
              id: b.id,
              name: b.property_name,
            }))}
          />
        </InviteWrap>
      )}

      {mode === 'password' && (
        <Form id="add-new-tenant-form" onSubmit={submit}>
          <Row>
            <Field>
              <Label htmlFor="tenant-first-name">First name</Label>
              <Input
                id="tenant-first-name"
                name="first_name"
                value={form.first_name}
                onChange={change}
                placeholder="Ada"
                required
              />
            </Field>
            <Field>
              <Label htmlFor="tenant-last-name">Last name</Label>
              <Input
                id="tenant-last-name"
                name="last_name"
                value={form.last_name}
                onChange={change}
                placeholder="Lovelace"
                required
              />
            </Field>
          </Row>
          <Field>
            <Label htmlFor="tenant-email">Email (login)</Label>
            <Input
              id="tenant-email"
              name="email"
              type="email"
              value={form.email}
              onChange={change}
              placeholder="resident@email.com"
              autoComplete="email"
              required
            />
          </Field>
          <Field>
            <Label htmlFor="tenant-password">Temporary password</Label>
            <Input
              id="tenant-password"
              name="password"
              type="password"
              value={form.password}
              onChange={change}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="tenant-password-confirm">Repeat password</Label>
            <Input
              id="tenant-password-confirm"
              name="password_confirm"
              type="password"
              value={form.password_confirm}
              onChange={change}
              placeholder="Enter the same password again"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="tenant-building">Building</Label>
            <Select
              id="tenant-building"
              name="building_id"
              value={form.building_id}
              onChange={change}
              required
            >
              <option value="">Select a building</option>
              {buildings.map(b => (
                <option key={b.id} value={b.id}>
                  {b.property_name}
                </option>
              ))}
            </Select>
          </Field>

          {/* Unfolds under the fields rather than popping in. */}
          <InlineMessage
            role="alert"
            message={error ? <ErrorText>{error}</ErrorText> : undefined}
          />
        </Form>
      )}
    </FormModal>
  );
};

const ModeTabs = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${palette.line};
  border-radius: ${radius.pill};
  margin-bottom: ${spacing.lg};
`;

const ModeTab = styled.button<{ active?: boolean }>`
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.md};
  border: none;
  border-radius: ${radius.pill};
  background: ${tabBg};
  color: ${tabColor};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
`;

const InviteWrap = styled.div`
  max-width: 560px;
`;

const Form = styled.form`
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.md};

  /* down('sm') (479.98px), not the hand-written 520px this carried. 520 sits
     between the sm and md tokens, so this row broke to one column at a width no
     other surface in the app changes at. onboarding.styled.tsx FieldRow is the
     same two-field grid and already reflows at sm — matching it is what makes a
     form built from both read as one form. */
  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${palette.slate};
`;

const baseInput = `
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  font: inherit;
  color: ${palette.ink};
  background: ${palette.white};
  outline: none;
`;

const Input = styled.input`
  ${baseInput}
  &:focus {
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const Select = styled.select`
  ${baseInput}
  &:focus {
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const ErrorText = styled.div`
  color: ${status.alert.fg};
  font-size: 13px;
`;

const Save = styled(AppButton)`
  min-width: 120px;
`;

const Cancel = styled(AppButton)``;

